import { z } from 'zod';
import { CABINET_LOCK_STATES, CABINET_PICKUP_STATUSES, CABINET_SCAN_RESULTS, CABINET_SNAPSHOT_EVENTS, } from '../constants/vend-states.js';
import { emailSchema, moneyCentsSchema, usPhoneSchema, uuidSchema } from './common.js';
/**
 * Glass-cabinet machines: a wall tablet next to a combination-locked cabinet.
 * The customer builds a cart, pays, is shown the code, takes the items and
 * scans each one so the sale is checked against what was paid for. Kept
 * apart from `vending.ts` because almost nothing is shared with the
 * dispensing flow beyond the machine row and the device token.
 */
// ------------------------------------------------------------ cameras
/**
 * An IP camera on the store's own network. The server never talks to it —
 * the tablet sits on the same network and pulls a still from `snapshotUrl`
 * itself, then uploads the JPEG. The URL may carry a view-only camera login
 * (Reolink's `cmd=Snap&user=…&password=…`), which is why it is only ever
 * served to the paired tablet and to staff holding `kiosks:manage`.
 */
export const machineCameraSchema = z.object({
    id: uuidSchema,
    machineId: uuidSchema,
    name: z.string(),
    snapshotUrl: z.string(),
    /** RTSP address the kiosk's Pi records short clips from; null = stills only.
     *  May carry the camera login, same as `snapshotUrl`. */
    streamUrl: z.string().nullable(),
    enabled: z.boolean(),
    createdAt: z.iso.datetime(),
});
/** Plain http only: the tablet's camera client deliberately speaks nothing
 *  else, and refuses any host that isn't a private LAN address. */
const snapshotUrlSchema = z
    .url({ protocol: /^http$/, message: 'Enter the camera’s http:// snapshot address' })
    .max(1000);
const streamUrlSchema = z
    .url({ protocol: /^rtsps?$/, message: 'Enter the camera’s rtsp:// stream address' })
    .max(1000);
export const createMachineCameraSchema = z.object({
    name: z.string().trim().min(1).max(80),
    snapshotUrl: snapshotUrlSchema,
    // No default on purpose: PATCH reuses this shape via `.partial()`, and a
    // default would blank a stored stream address on every unrelated edit.
    streamUrl: streamUrlSchema.nullable().optional(),
    enabled: z.boolean().default(true),
});
export const updateMachineCameraSchema = createMachineCameraSchema.partial();
/** What the tablet needs to grab a still — nothing about who set it up. */
export const cabinetTerminalCameraSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    snapshotUrl: z.string(),
});
// ----------------------------------------------------------- terminal
export const cabinetCatalogProductSchema = z.object({
    productId: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    priceCents: moneyCentsSchema,
    salePriceCents: moneyCentsSchema.nullable(),
    imageUrl: z.string().nullable(),
    /** A phone or SIM — one per cart line, and the customer is told which
     *  exact box to take. */
    unitTracked: z.boolean(),
    availableQty: z.number().int().nonnegative(),
});
export const cabinetCatalogSchema = z.object({
    machineName: z.string(),
    /** False until staff set a combination — the tablet shows an
     *  out-of-service screen rather than selling something nobody can open. */
    ready: z.boolean(),
    cardEntryFallback: z.boolean(),
    startScreenLogoUrl: z.string().nullable(),
    startScreenMessage: z.string().nullable(),
    cameras: z.array(cabinetTerminalCameraSchema),
    products: z.array(cabinetCatalogProductSchema),
});
export const cabinetCheckoutSchema = z
    .object({
    items: z
        .array(z.object({ productId: uuidSchema, qty: z.number().int().min(1).max(10) }))
        .min(1)
        .max(20),
    idempotencyKey: z.string().trim().min(8).max(100),
    buyerName: z.string().trim().min(1).max(150).nullable().default(null),
    buyerPhone: usPhoneSchema.nullable().default(null),
    createAccount: z.boolean().default(false),
    email: emailSchema.nullable().default(null),
    /** Ticked "text me order updates" under the phone number. Saved as the
     *  account's text consent when there is an account to save it on. */
    smsOptIn: z.boolean().default(false),
    /** Gift card codes typed at the tablet; what they cover comes off the
     *  amount held on the card. */
    giftCardCodes: z.array(z.string().trim().min(1).max(64)).max(5).default([]),
    /** A promo code typed at the tablet (t1294); judged again at checkout. */
    couponCode: z.string().trim().min(1).max(64).nullable().default(null),
})
    .refine((v) => !v.smsOptIn || v.buyerPhone, {
    message: 'Enter a phone number to receive texts',
    path: ['buyerPhone'],
})
    .refine((v) => !v.createAccount || (v.email && v.buyerName && v.buyerPhone), {
    message: 'Name, phone and email are all required to create an account',
    path: ['email'],
})
    .refine((v) => new Set(v.items.map((i) => i.productId)).size === v.items.length, {
    message: 'Each product may appear in the cart only once',
    path: ['items'],
});
/** "Add coupon" on the tablet's checkout: is this code usable for this cart,
 *  and what does it take off? */
export const cabinetCouponCheckSchema = z.object({
    code: z.string().trim().min(1).max(64),
    items: z
        .array(z.object({ productId: uuidSchema, qty: z.number().int().min(1).max(10) }))
        .min(1)
        .max(20),
});
export const cabinetCouponCheckResultSchema = z.object({
    code: z.string(),
    discountCents: z.number().int(),
});
/** Switching the order's PaymentIntent between a tap on the tablet and a
 *  typed card number. Both settle through the same webhook. */
export const cabinetCardEntrySchema = z.object({
    method: z.enum(['card_present', 'card']),
});
export const cabinetCardEntryResultSchema = z.object({
    clientSecret: z.string(),
    publishableKey: z.string().nullable(),
});
export const cabinetPickupLineSchema = z.object({
    productId: uuidSchema,
    name: z.string(),
    qty: z.number().int().positive(),
    scannedQty: z.number().int().nonnegative(),
    /** For a phone or SIM: which box to take, e.g. "IMEI ending 4821". */
    unitHint: z.string().nullable(),
    unitPriceCents: moneyCentsSchema,
    /** Set on a SIM line: lets the tablet offer "add a plan to this SIM". */
    simUnitId: uuidSchema.nullable().default(null),
});
export const cabinetReceiptSchema = z.object({
    shopName: z.string(),
    shopAddressLines: z.array(z.string()),
    shopPhone: z.string().nullable(),
    machineName: z.string(),
    orderNumber: z.string(),
    paidAt: z.iso.datetime().nullable(),
    lines: z.array(z.object({
        name: z.string(),
        qty: z.number().int(),
        unitPriceCents: moneyCentsSchema,
        lineTotalCents: moneyCentsSchema,
        /** The full IMEI/ICCID — warranty and carrier activation need it. */
        identifier: z.string().nullable(),
    })),
    subtotalCents: moneyCentsSchema,
    taxCents: moneyCentsSchema,
    totalCents: moneyCentsSchema,
});
/** "Text my receipt" / "Email my receipt" on the done screen. */
export const cabinetReceiptSendSchema = z.discriminatedUnion('method', [
    z.object({ method: z.literal('sms'), to: usPhoneSchema }),
    z.object({ method: z.literal('email'), to: emailSchema }),
]);
export const cabinetOrderStatusSchema = z.object({
    orderId: uuidSchema,
    orderNumber: z.string(),
    /** `held`: the card is authorized but not charged — that happens once every
     *  item has been scanned. */
    payment: z.enum(['pending', 'held', 'paid', 'failed']),
    pickup: z.enum(CABINET_PICKUP_STATUSES).nullable(),
    /** Only while the customer may still collect — never after confirmation. */
    cabinetCode: z.string().nullable(),
    lines: z.array(cabinetPickupLineSchema),
    allScanned: z.boolean(),
    lockState: z.enum(CABINET_LOCK_STATES).nullable(),
    /** Customer-facing copy, rendered verbatim. */
    message: z.string().nullable(),
    receipt: cabinetReceiptSchema.nullable(),
});
export const cabinetScanSchema = z.object({
    code: z.string().trim().min(1).max(64),
});
export const cabinetScanResultSchema = z.object({
    result: z.enum(CABINET_SCAN_RESULTS),
    message: z.string(),
    status: cabinetOrderStatusSchema,
});
export const cabinetLockSchema = z.object({ locked: z.boolean() });
/** ~2 MB of JPEG once base64-encoded — a 4MP still is a few hundred KB. */
export const MAX_CABINET_SNAPSHOT_BASE64 = 2_800_000;
export const cabinetSnapshotUploadSchema = z.object({
    event: z.enum(CABINET_SNAPSHOT_EVENTS),
    cameraId: uuidSchema.nullable().default(null),
    capturedAt: z.iso.datetime(),
    imageBase64: z.string().min(100).max(MAX_CABINET_SNAPSHOT_BASE64),
});
// -------------------------------------------------------------- admin
export const cabinetPickupSchema = z.object({
    id: uuidSchema,
    orderId: uuidSchema,
    orderNumber: z.string(),
    status: z.enum(CABINET_PICKUP_STATUSES),
    lockState: z.enum(CABINET_LOCK_STATES).nullable(),
    totalCents: moneyCentsSchema,
    buyerName: z.string().nullable(),
    openedAt: z.iso.datetime(),
    confirmedAt: z.iso.datetime().nullable(),
    timedOutAt: z.iso.datetime().nullable(),
    /** `held`: the customer's card is authorized and waiting on staff or a scan. */
    paymentState: z.enum(['held', 'charged', 'released']),
    scans: z.array(z.object({
        code: z.string(),
        result: z.enum(CABINET_SCAN_RESULTS),
        productName: z.string().nullable(),
        createdAt: z.iso.datetime(),
    })),
    snapshots: z.array(z.object({
        id: uuidSchema,
        event: z.enum(CABINET_SNAPSHOT_EVENTS),
        cameraName: z.string().nullable(),
        capturedAt: z.iso.datetime(),
    })),
});
//# sourceMappingURL=cabinet.js.map